import React, { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight, CalendarBlank, ListDashes } from "@phosphor-icons/react";

/**
 * My Schedule — calendar view of the signed-in member's tours.
 *
 * Two viewing modes controlled by the toggle at the top-right:
 *   • 7-Day View  — one column per day, Sunday..Saturday
 *   • Month View  — full calendar grid for the current month
 *
 * Both views print each shift's date + military time + position and
 * shade any tour whose end has already passed in light grey (spec #8).
 */

const CERT_COLOR = {
  Trainee: "bg-slate-500 text-white",
  Driver: "bg-amber-600 text-white",
  EMT: "bg-blue-600 text-white",
  Paramedic: "bg-purple-600 text-white",
  Supervisor: "bg-slate-900 text-white",
};

function pad(n) { return String(n).padStart(2, "0"); }
// ET-anchored helpers. Every date bucket + military-time label reads
// the shift in Eastern Time so a 00:00 ET shift lands on its ET
// calendar date regardless of the operator's browser timezone.
const _ET_TZ = "America/New_York";
function _etDateParts(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const fmt = new Intl.DateTimeFormat("en-CA", {
    timeZone: _ET_TZ,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
  const p = Object.fromEntries(fmt.formatToParts(d).map((x) => [x.type, x.value]));
  if (p.hour === "24") p.hour = "00";
  return { year: p.year, month: p.month, day: p.day, hour: p.hour, minute: p.minute };
}
function milHM(iso) {
  const p = _etDateParts(iso);
  return p ? `${p.hour}${p.minute}` : "----";
}
function etDayKey(iso) {
  const p = _etDateParts(iso);
  return p ? `${p.year}-${p.month}-${p.day}` : "";
}
function dateToDayKey(d) {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function fmtDate(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function startOfWeek(d) {
  const c = new Date(d); c.setHours(0, 0, 0, 0);
  c.setDate(c.getDate() - c.getDay()); // Sunday
  return c;
}
function addDays(d, n) { const c = new Date(d); c.setDate(c.getDate() + n); return c; }
function startOfMonth(d) { const c = new Date(d); c.setHours(0, 0, 0, 0); c.setDate(1); return c; }
function endOfMonth(d) { const c = new Date(d); c.setMonth(c.getMonth() + 1, 0); c.setHours(23, 59, 59, 999); return c; }
function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** Shift tile — used by both calendar views. Grey-shaded once the
 *  shift's end has passed (spec #8) but details stay readable. */
function ShiftTile({ tour, dense }) {
  const past = new Date(tour.end_time).getTime() < Date.now();
  return (
    <div
      className={`rounded-sm border px-2 py-1 mb-1 text-[11px] leading-tight ${past ? "bg-slate-100 border-slate-200 text-slate-500" : "bg-white border-slate-300 text-slate-800"}`}
      data-testid={`my-shift-${tour.id}${past ? "-past" : ""}`}
      title={past ? "Past shift" : ""}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-semibold mono">{milHM(tour.start_time)}–{milHM(tour.end_time)}</span>
        {tour.required_certification && (
          <Badge className={`rounded-sm text-[9px] px-1 py-0 ${past ? "bg-slate-400 text-white" : (CERT_COLOR[tour.required_certification] || "bg-blue-600 text-white")}`}>
            {tour.required_certification}
          </Badge>
        )}
      </div>
      <div className={`font-medium truncate ${past ? "" : "text-slate-900"}`}>{tour.position || tour.title || "—"}</div>
      {!dense && tour.ambulance_role && (
        <div className="text-[10px] text-slate-500 mono">{tour.ambulance_role === "Head Tech" && /^SPECIAL EVENT\b/i.test(tour.position || "") ? "EMT" : tour.ambulance_role}</div>
      )}
      {!dense && tour.unit && <div className="text-[10px] text-slate-500 mono">Unit {tour.unit}</div>}
    </div>
  );
}

function WeekView({ anchor, tours, onPrev, onNext }) {
  const week = useMemo(() => {
    const s = startOfWeek(anchor);
    return Array.from({ length: 7 }, (_, i) => addDays(s, i));
  }, [anchor]);
  const byDay = useMemo(() => {
    const map = new Map(week.map((d) => [dateToDayKey(d), []]));
    for (const t of tours) {
      const key = etDayKey(t.start_time);
      if (map.has(key)) map.get(key).push(t);
    }
    for (const list of map.values()) list.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [week, tours]);

  const today = new Date();
  return (
    <div className="surface-grid">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
        {week.map((d) => (
          <div
            key={d.toISOString()}
            className={`px-2 py-2 text-xs font-semibold uppercase tracking-wide border-r last:border-r-0 border-slate-200 ${sameDay(d, today) ? "text-emerald-700" : "text-slate-600"}`}
            data-testid={`week-head-${d.toISOString().slice(0, 10)}`}
          >
            <div>{d.toLocaleDateString(undefined, { weekday: "short" })}</div>
            <div className="mono font-normal text-[11px] text-slate-500">{d.toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 min-h-[420px]">
        {week.map((d) => {
          const list = byDay.get(dateToDayKey(d)) || [];
          return (
            <div key={d.toISOString()} className="border-r last:border-r-0 border-slate-200 p-1.5" data-testid={`week-cell-${dateToDayKey(d)}`}>
              {list.length === 0 ? (
                <div className="text-[10px] text-slate-400 italic p-1">No shifts</div>
              ) : list.map((t) => <ShiftTile key={t.id} tour={t} />)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function MonthView({ anchor, tours }) {
  // Build a 5- or 6-row grid starting at the Sunday of the first week
  // that contains the 1st of the month, ending on the Saturday of the
  // week that contains the last day.
  const gridStart = startOfWeek(startOfMonth(anchor));
  const monthEnd = endOfMonth(anchor);
  const gridEnd = addDays(startOfWeek(monthEnd), 6);
  const days = useMemo(() => {
    const out = [];
    for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) out.push(d);
    return out;
  }, [gridStart.getTime(), gridEnd.getTime()]);

  const byDay = useMemo(() => {
    const map = new Map();
    for (const t of tours) {
      const key = etDayKey(t.start_time);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    for (const list of map.values()) list.sort((a, b) => a.start_time.localeCompare(b.start_time));
    return map;
  }, [tours]);

  const today = new Date();
  const monthNum = anchor.getMonth();

  return (
    <div className="surface-grid">
      <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <div key={d} className="px-2 py-1.5 border-r last:border-r-0 border-slate-200">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const list = byDay.get(dateToDayKey(d)) || [];
          const inMonth = d.getMonth() === monthNum;
          const isToday = sameDay(d, today);
          return (
            <div
              key={d.toISOString()}
              className={`min-h-[120px] border-r border-b last:border-r-0 border-slate-200 p-1 ${inMonth ? "bg-white" : "bg-slate-50/60"}`}
              data-testid={`month-cell-${d.toISOString().slice(0, 10)}`}
            >
              <div className={`flex items-center justify-between mb-1 ${inMonth ? "text-slate-900" : "text-slate-400"}`}>
                <span className={`mono text-xs font-semibold ${isToday ? "bg-emerald-600 text-white rounded-full w-6 h-6 inline-flex items-center justify-center" : ""}`}>
                  {d.getDate()}
                </span>
                {list.length > 0 && <span className="text-[9px] text-slate-500">{list.length}</span>}
              </div>
              {list.slice(0, 3).map((t) => <ShiftTile key={t.id} tour={t} dense />)}
              {list.length > 3 && <div className="text-[10px] text-slate-500 pl-1">+{list.length - 3} more</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function MyTours() {
  const [tours, setTours] = useState([]);
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(new Date());

  useEffect(() => { api.get("/tours/mine").then((r) => setTours(r.data)); }, []);

  const subtitle = view === "week"
    ? `Week of ${fmtDate(startOfWeek(anchor))} – ${fmtDate(addDays(startOfWeek(anchor), 6))}`
    : anchor.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  function step(dir) {
    setAnchor((prev) => {
      const c = new Date(prev);
      if (view === "week") c.setDate(c.getDate() + dir * 7);
      else c.setMonth(c.getMonth() + dir);
      return c;
    });
  }

  return (
    <AppLayout
      title="My Schedule"
      subtitle={subtitle}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          <div className="border border-slate-200 rounded-sm overflow-hidden flex" data-testid="my-view-toggle">
            <button
              type="button"
              onClick={() => setView("week")}
              className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "week" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="my-view-week"
            >
              <ListDashes size={12} weight="bold" /> 7-DAY
            </button>
            <button
              type="button"
              onClick={() => setView("month")}
              className={`px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1 ${view === "month" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}
              data-testid="my-view-month"
            >
              <CalendarBlank size={12} weight="bold" /> MONTH
            </button>
          </div>
          <Button variant="outline" size="icon" onClick={() => step(-1)} className="rounded-sm" data-testid="my-prev"><CaretLeft size={14} weight="bold" /></Button>
          <Button variant="outline" onClick={() => setAnchor(new Date())} className="rounded-sm h-9 text-xs" data-testid="my-today">TODAY</Button>
          <Button variant="outline" size="icon" onClick={() => step(1)} className="rounded-sm" data-testid="my-next"><CaretRight size={14} weight="bold" /></Button>
        </div>
      }
    >
      <div className="mb-3 flex items-center gap-3 text-[11px] text-slate-500">
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-slate-100 border border-slate-300 rounded-sm" /> Past shift</span>
        <span className="inline-flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-white border border-slate-300 rounded-sm" /> Upcoming</span>
        <span className="mono ml-auto">{tours.length} total tour(s)</span>
      </div>

      {view === "week"
        ? <WeekView anchor={anchor} tours={tours} onPrev={() => step(-1)} onNext={() => step(1)} />
        : <MonthView anchor={anchor} tours={tours} />
      }
    </AppLayout>
  );
}
